import React from 'react'
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogContent,
    IconButton,
    Paper,
    Stack,
    Typography
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined'
import LaunchIcon from '@mui/icons-material/Launch'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import { PDPC_DATA_PROTECTION_OBLIGATIONS_URL } from './qualityReviewPrivacy'

export default function QualityReviewPrivacyDialog({ open, onClose, policyStatus = 'disabled' }) {
    const retentionEnabled = policyStatus === 'enabled'
    const policyResolved = retentionEnabled || policyStatus === 'disabled'
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            aria-labelledby="quality-review-privacy-title"
            PaperProps={{ sx: { borderRadius: { xs: 2, sm: 3 }, m: { xs: 1.5, sm: 3 } } }}
        >
            <DialogContent sx={{ p: 0 }}>
                <Box
                    sx={{
                        position: 'relative',
                        px: { xs: 2.5, md: 4 },
                        py: { xs: 3, md: 4 },
                        color: '#ffffff',
                        background: 'linear-gradient(135deg, #123a4c 0%, #1f665a 100%)'
                    }}
                >
                    <IconButton
                        aria-label="Close Privacy and PDPA information"
                        onClick={onClose}
                        sx={{ position: 'absolute', right: 12, top: 12, color: '#ffffff' }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <GppGoodOutlinedIcon sx={{ fontSize: 38, mb: 1.25 }} />
                    <Typography id="quality-review-privacy-title" variant="h4" sx={{ fontWeight: 900, pr: 5 }}>
                        Privacy &amp; PDPA
                    </Typography>
                    <Typography sx={{ mt: 1, maxWidth: 700, color: 'rgba(255,255,255,0.88)', lineHeight: 1.65 }}>
                        Quality Review is designed to support compliance with Singapore’s Personal Data Protection Act through data minimisation, purpose-limited processing and restricted access.
                    </Typography>
                </Box>

                <Stack spacing={2.25} sx={{ p: { xs: 2.5, md: 4 } }}>
                    <Alert severity="info" sx={{ borderRadius: 2.5 }}>
                        {!policyResolved
                            ? 'Source-file handling has not yet been confirmed. The quality check cannot start until this policy check succeeds.'
                            : retentionEnabled
                            ? 'All uploaded files are encrypted and kept for 14 days so authorised reviewers can verify the review, including possible missed issues. They are not antivirus-scanned, so open or download them only on a protected work device. They are then deleted automatically. Review results remain for up to 30 days.'
                            : 'Documents uploaded for a review are used only to run that review. Original files are not retained after processing; review results remain for up to 30 days.'}
                    </Alert>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                            gap: 1.5
                        }}
                    >
                        <PrivacyCard
                            icon={<DeleteOutlineIcon />}
                            title={!policyResolved ? 'Source-file policy not confirmed' : retentionEnabled ? 'Fourteen-day source-file access' : 'No source-file archive'}
                            description={!policyResolved
                                ? 'Return to Quality Check and retry the policy check before starting a review.'
                                : retentionEnabled
                                ? 'Every uploaded BizFile and review document is retained. They are available to the uploader and at most one selected colleague, then deleted automatically. Bubbly does not antivirus-scan these files.'
                                : 'Original uploads and corrected document copies are not retained after processing, so later source-file download is unavailable.'}
                        />
                        <PrivacyCard
                            icon={<StorageOutlinedIcon />}
                            title="Results kept separately"
                            description="Filenames, reporting period, year of assessment, issue locations, review status and reviewer decisions remain for up to 30 days by default, then are deleted automatically."
                        />
                        <PrivacyCard
                            icon={<LockOutlinedIcon />}
                            title={!policyResolved || retentionEnabled ? 'Restricted access' : 'Local source access'}
                            description={!policyResolved
                                ? 'No retention assumption is made while the policy is unknown, and the quality check remains disabled.'
                                : retentionEnabled
                                ? 'Every later view or download requires sign-in, authenticator verification and permission for that specific review. No public file link is created. Bubbly cannot remotely erase a file already opened or downloaded into a browser or device.'
                                : 'During the active browser session, selected files can be opened from the local browser file object. Saved reviews point staff to their own working copy.'}
                        />
                        <PrivacyCard
                            icon={<GppGoodOutlinedIcon />}
                            title="Purpose-limited processing"
                            description="Uploaded content is used only for the quality check and fourteen-day authorised review access, and is not sent to external AI services."
                        />
                    </Box>

                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, backgroundColor: '#fffaf0', borderColor: '#ead8a7' }}>
                        <Stack direction="row" spacing={1.25} alignItems="flex-start">
                            <WarningAmberOutlinedIcon sx={{ color: '#9a6815', mt: 0.15 }} />
                            <Box>
                                <Typography sx={{ fontWeight: 850, color: '#684814' }}>
                                    What this means for your organisation
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#684814', mt: 0.5, lineHeight: 1.65 }}>
                                    These safeguards support PDPA compliance; they are not a certification or legal guarantee. Your organisation remains responsible for having a lawful purpose, uploading only necessary documents, assigning authorised access, checking result accuracy, correcting its own working copies and meeting any legal or professional retention duties.
                                </Typography>
                            </Box>
                        </Stack>
                    </Paper>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
                        <Button
                            variant="outlined"
                            endIcon={<LaunchIcon />}
                            component="a"
                            href={PDPC_DATA_PROTECTION_OBLIGATIONS_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
                        >
                            Singapore PDPC obligations
                        </Button>
                        <Button variant="contained" onClick={onClose} sx={{ minWidth: 110 }}>
                            Done
                        </Button>
                    </Stack>
                </Stack>
            </DialogContent>
        </Dialog>
    )
}

function PrivacyCard({ icon, title, description }) {
    return (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, borderColor: '#dbe6e8', height: '100%' }}>
            <Stack direction="row" spacing={1.25} alignItems="flex-start">
                <Box sx={{ color: '#1f665a', display: 'flex', mt: 0.15 }}>{icon}</Box>
                <Box>
                    <Typography sx={{ fontWeight: 850, color: '#173042' }}>{title}</Typography>
                    <Typography variant="body2" sx={{ color: '#506776', mt: 0.5, lineHeight: 1.65 }}>
                        {description}
                    </Typography>
                </Box>
            </Stack>
        </Paper>
    )
}
